import { theme } from 'antd';
import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { teamsActions } from 'reduxStore/teams/slice';
import { Notification } from 'zComponents/atoms';
import { Button, Form, Input, Modal, Select } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { TextArea } = Input;

const EditTeamModal = ({ team, children }) => {
	// hooks
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const {
		token: { colorTextDescription }
	} = theme.useToken();

	//redux state
	const { data: sitesData } = useSelector((state) => state.Sites);

	//local state
	const [loading, setLoading] = useState(false);
	const [buttonDisable, setButtonDisable] = useState(true);
	const [visible, setVisible] = useState(false);

	const isPermittedUpdateTeam = useManagePermittedRoles('updateTeam', {
		orgID: [team.orgID],
		siteID: [team.siteID],
		teamID: [team.teamID]
	});

	const handleCancel = () => {
		setVisible(false);
		setLoading(false);
		form.resetFields();
	};

	const onFinish = async () => {
		const formData = removeEmptyOrNull(form.getFieldsValue(true));

		const body = {
			name: formData.name,
			description: formData.description,
			siteID: formData.siteID
		};
		try {
			const res = await SuperFetch.patch('/teams/' + team.teamID, body);
			if (res) {
				if (res.statusCode === 200) {
					Notification('success', 'Team Information Updated.');
				}
				dispatch(teamsActions.loadTeams());
				handleCancel();
			}
		} catch (error) {
			Notification('error', 'Update Failed, Please try again.');
		}
	};

	const updateChange = () => {
		const formData = form.getFieldsValue(true);
		return (
			(team.name === formData.name || formData.name === '') &&
			(team.description === formData.description || formData.description === '') &&
			(team.siteID === formData.siteID || formData.siteID === '')
		);
	};

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={'Edit Team'}
				condition={!isPermittedUpdateTeam}
				disabledCondition={!isPermittedUpdateTeam}
				trueTitle={"You don't have permission"}
				falseTitle={'Edit this Team’s Information for their Profile'}
				onClick={() => setVisible(true)}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				title='Edit Team'
				visible={visible}
				onCancel={handleCancel}
				destroyOnClose={true}
				footer={[
					<Button key='back' onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						form={form}
						key='submit'
						htmlType='submit'
						type='primary'
						loading={loading}
						disabled={buttonDisable}
					>
						Update
					</Button>
				]}
			>
				<Form
					form={form}
					id={form}
					labelCol={{ lg: { span: 6, offset: 0 }, sm: { span: 6, offset: 0 } }}
					wrapperCol={{ lg: { span: 17 }, sm: { span: 16 }, xs: { span: 10 } }}
					onFinish={onFinish}
					initialValues={team}
					autoComplete='off'
					onValuesChange={() => setButtonDisable(updateChange())}
				>
					<Form.Item label='Name' name='name' rules={fieldsValidator.Name}>
						<Input />
					</Form.Item>
					<Form.Item label='Description' name='description' rules={fieldsValidator.Description}>
						<TextArea
							style={{ color: colorTextDescription }}
							allowClear
							autoSize={{ minRows: 2, maxRows: 6 }}
						/>
					</Form.Item>
					<Form.Item name='siteID' label='Site'>
						<Select
							placeholder={'Select Site'}
							options={sitesData.map((site) => ({
								label: site.name,
								value: site.siteID
							}))}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default EditTeamModal;
